import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CheckCircle2, Eye, Flag } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { MobileSortBar, SortableTh, TableSearchInput, useTableSort } from "@/components/ui/TableSort";
import { naturalCompare } from "@/utils/naturalCompare";
import { ordersApi } from "../api/orders";
import { useContractorCompleteMilestone } from "../hooks/useOrders";
import {
  ORDER_STATUS_LABELS,
  type ContractorTodoData,
  type ContractorTodoItem,
  type ContractorTodoPreviewMeta,
} from "../types";

/**
 * „Moje terminy" — zbiorcze zestawienie kamieni milowych i terminów końcowych
 * wszystkich aktywnych zamówień JEDNEJ firmy: chipy pilności jako filtr,
 * szukajka + filtry zamówienia/typu, sortowalna tabela (standard TableSort).
 * Kolumna „Status": termin końcowy niesie status zamówienia, kamień — własny
 * stan (W trakcie / Wykonane) i akcję „Oznacz jako ukończony" za modalem
 * potwierdzenia; po niej kamień jest wykonany także w zamówieniu (ten sam
 * rekord), więc oznaczać mogą i zlecający, i podwykonawca. Gdy zlecający cofnie
 * wykonanie, kamień wraca do „W trakcie" z dopiskiem „cofnięte przez
 * zlecającego" i akcja jest znów dostępna.
 *
 * Bez `contractorId` — portal podwykonawcy (firma z zalogowanego konta).
 * Z `contractorId` — podgląd GK (dyr. techniczny / CEO) w kartotece firmy:
 * ten sam payload, który widzi firma (parytet pilnuje backendowy test),
 * plus baner „tak widzi to firma X". Wiersze linkują wtedy do widoku GK
 * zamówienia zamiast do portalu podwykonawcy.
 */

type Bucket = "overdue" | "urgent" | "upcoming" | "done";
type BucketFilter = "open" | Bucket | "all";

// Ostatnio wybrany chip pilności to preferencja przeglądarki (localStorage) —
// ma przeżyć F5 i powrót następnego dnia, u podwykonawcy i w podglądzie GK.
const BUCKET_STORE_KEY = "contractor-todo:bucket";
const BUCKET_FILTERS: BucketFilter[] = ["open", "overdue", "urgent", "upcoming", "done", "all"];

function readStoredBucket(): BucketFilter {
  try {
    const raw = localStorage.getItem(BUCKET_STORE_KEY);
    return BUCKET_FILTERS.includes(raw as BucketFilter) ? (raw as BucketFilter) : "all";
  } catch {
    return "all";
  }
}
type SortField = "due_date" | "label" | "order_number" | "localities" | "order_status";

const SORT_LABELS: Record<SortField, string> = {
  due_date: "Termin",
  label: "Pozycja",
  order_number: "Zamówienie",
  localities: "Miejscowość",
  order_status: "Status",
};

// Kolumna „Status": dla terminu końcowego status zamówienia, dla kamienia jego
// własny stan — po tej etykiecie też sortujemy.
function statusLabel(item: ContractorTodoItem): string {
  if (item.kind === "milestone") return item.is_done ? "Wykonane" : "W trakcie";
  return ORDER_STATUS_LABELS[item.order_status];
}

function bucketOf(item: ContractorTodoItem, urgentWindow: number): Bucket {
  if (item.is_done) return "done";
  const d = item.days_left ?? 0;
  if (d < 0) return "overdue";
  if (d <= urgentWindow) return "urgent";
  return "upcoming";
}

function compareItems(a: ContractorTodoItem, b: ContractorTodoItem, field: SortField): number {
  switch (field) {
    case "due_date":
      return a.due_date.localeCompare(b.due_date);
    case "label":
      return a.label.localeCompare(b.label, "pl");
    case "order_number":
      return naturalCompare(a.order_number, b.order_number);
    case "localities":
      return a.localities.join(", ").localeCompare(b.localities.join(", "), "pl");
    case "order_status":
      return statusLabel(a).localeCompare(statusLabel(b), "pl");
  }
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function ContractorTodoView({ contractorId }: { contractorId?: string }) {
  const isPreview = contractorId !== undefined;
  const query = useQuery({
    queryKey: isPreview ? ["contractor-todo-preview", contractorId] : ["contractor-todo"],
    queryFn: async (): Promise<{ data: ContractorTodoData; meta: ContractorTodoPreviewMeta | null }> => {
      if (isPreview) return ordersApi.contractorTodoPreview(contractorId).then((r) => ({ ...r, meta: r.meta }));
      return { data: await ordersApi.contractorTodo(), meta: null };
    },
  });

  const [bucket, setBucketState] = useState<BucketFilter>(readStoredBucket);
  const setBucket = (next: BucketFilter) => {
    setBucketState(next);
    try {
      localStorage.setItem(BUCKET_STORE_KEY, next);
    } catch {
      // Best-effort — filtr i tak działa do końca montażu.
    }
  };
  const [search, setSearch] = useState("");
  const [filterOrder, setFilterOrder] = useState("");
  const [filterKind, setFilterKind] = useState<"" | "milestone" | "order_deadline">("");
  const { sortField, sortDir, handleSort, setField, toggleDir } = useTableSort<SortField>();

  // „Oznacz jako ukończony" zawsze przez modal — kamień w zamówieniu odhacza się
  // tym samym ruchem, a cofnąć to może już tylko zlecający.
  const [confirmItem, setConfirmItem] = useState<ContractorTodoItem | null>(null);
  const complete = useContractorCompleteMilestone();
  const closeConfirm = () => {
    if (complete.isPending) return;
    setConfirmItem(null);
    complete.reset();
  };
  const confirmComplete = () => {
    if (!confirmItem) return;
    complete.mutate(
      { orderId: confirmItem.order_id, milestoneId: confirmItem.id },
      { onSuccess: () => setConfirmItem(null) },
    );
  };

  const data = query.data?.data;
  const urgentWindow = data?.urgent_window_days ?? 7;

  const orderOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of data?.items ?? []) seen.set(item.order_id, item.order_number);
    return [...seen.entries()].sort((a, b) => naturalCompare(a[1], b[1]));
  }, [data]);

  const visible = useMemo(() => {
    let list = data?.items ?? [];
    if (bucket === "open") list = list.filter((i) => !i.is_done);
    else if (bucket !== "all") list = list.filter((i) => bucketOf(i, urgentWindow) === bucket);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.label.toLowerCase().includes(q) ||
          i.order_number.toLowerCase().includes(q) ||
          i.localities.some((l) => l.toLowerCase().includes(q)),
      );
    }
    if (filterOrder) list = list.filter((i) => i.order_id === filterOrder);
    if (filterKind) list = list.filter((i) => i.kind === filterKind);
    if (!sortField) return list; // domyślnie: porządek pilności z backendu
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => dir * compareItems(a, b, sortField));
  }, [data, bucket, urgentWindow, search, filterOrder, filterKind, sortField, sortDir]);

  if (query.isLoading) return <PageLoader />;
  if (query.isError || !data) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Nie udało się pobrać zestawienia terminów. Odśwież stronę lub spróbuj później.
      </p>
    );
  }

  const meta = query.data?.meta ?? null;
  const { counts } = data;
  const openCount = counts.overdue + counts.urgent + counts.upcoming;
  const hasActiveFilters = !!search || !!filterOrder || !!filterKind;

  const chips: { key: BucketFilter; label: string; count: number; tone?: "red" | "amber" }[] = [
    { key: "open", label: "Otwarte", count: openCount },
    { key: "overdue", label: "Po terminie", count: counts.overdue, tone: "red" },
    { key: "urgent", label: `Pilne ≤${urgentWindow} dn.`, count: counts.urgent, tone: "amber" },
    { key: "upcoming", label: "Nadchodzące", count: counts.upcoming },
    { key: "done", label: "Wykonane", count: counts.done },
    { key: "all", label: "Wszystkie", count: data.items.length },
  ];

  return (
    <div>
      {isPreview && (
        <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 px-3 py-2 mb-4 text-sm text-amber-800 dark:text-amber-200">
          <Eye size={16} className="shrink-0" />
          <span>
            Podgląd — dokładnie tak to zestawienie widzi firma
            {meta ? <strong> {meta.contractor_name}</strong> : ""} po zalogowaniu. Tylko do odczytu.
          </span>
        </div>
      )}

      {data.items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Brak terminów — żadne aktywne zamówienie nie ma kamieni milowych ani terminu końcowego.
        </p>
      ) : (
        <>
          {/* Chipy pilności = filtr; liczniki zawsze dla całego zestawienia. */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {chips.map((c) => {
              const active = bucket === c.key;
              const toneCls =
                c.tone === "red" && c.count > 0
                  ? "text-red-700 dark:text-red-400"
                  : c.tone === "amber" && c.count > 0
                    ? "text-amber-700 dark:text-amber-400"
                    : "text-gray-600 dark:text-gray-300";
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setBucket(c.key)}
                  aria-pressed={active}
                  className={`text-xs px-2.5 py-1.5 border transition-colors ${
                    active
                      ? "border-[var(--color-primary)] bg-red-50 dark:bg-red-900/20 text-[var(--color-primary)] font-semibold"
                      : `border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-400 ${toneCls}`
                  }`}
                >
                  {c.key === "overdue" && <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />}
                  {c.label} <span className="tabular-nums font-semibold">({c.count})</span>
                </button>
              );
            })}
          </div>

          {/* Toolbar: szukajka + zawężenie do zamówienia / typu pozycji. */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="[&>div]:mb-0">
              <TableSearchInput
                value={search}
                onChange={setSearch}
                placeholder="Szukaj: pozycja, nr zamówienia, miejscowość…"
              />
            </div>
            <select
              value={filterOrder}
              onChange={(e) => setFilterOrder(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-600 px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            >
              <option value="">Zamówienie: wszystkie</option>
              {orderOptions.map(([id, number]) => (
                <option key={id} value={id}>{number}</option>
              ))}
            </select>
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value as typeof filterKind)}
              className="text-xs border border-gray-200 dark:border-gray-600 px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            >
              <option value="">Typ: wszystkie</option>
              <option value="milestone">Kamień milowy</option>
              <option value="order_deadline">Termin końcowy</option>
            </select>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => { setSearch(""); setFilterOrder(""); setFilterKind(""); }}
                className="text-xs text-gray-500 dark:text-gray-400 underline hover:text-[var(--color-primary)]"
              >
                Wyczyść filtry
              </button>
            )}
          </div>

          <MobileSortBar
            id="contractor-todo-sort"
            labels={SORT_LABELS}
            sortField={sortField}
            sortDir={sortDir}
            onFieldChange={setField}
            onDirToggle={toggleDir}
          />

          {visible.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
              Nic nie pasuje do wybranych filtrów.
            </p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <table className="w-full text-xs min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                    <SortableTh field="due_date" label="Termin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="label" label="Pozycja" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="order_number" label="Zamówienie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="localities" label="Miejscowość" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="order_status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <TodoRow
                      key={item.id}
                      item={item}
                      isPreview={isPreview}
                      urgentWindow={urgentWindow}
                      // Podgląd GK jest do odczytu — bez akcji.
                      onComplete={isPreview ? undefined : setConfirmItem}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {confirmItem && (
        <Modal open title="Ukończenie kamienia" onClose={closeConfirm} size="sm">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Oznaczyć kamień <strong>„{confirmItem.label}”</strong> z zamówienia{" "}
              <strong>{confirmItem.order_number}</strong> jako ukończony?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Kamień zostanie oznaczony jako wykonany z dzisiejszą datą także w zamówieniu.
              Cofnąć to może już tylko zlecający.
            </p>
            {complete.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {(complete.error as { response?: { data?: { errors?: { detail?: string } } } })
                  ?.response?.data?.errors?.detail ?? "Nie udało się zapisać. Spróbuj ponownie."}
              </p>
            )}
            <div className="flex gap-3">
              <Button loading={complete.isPending} onClick={confirmComplete}>
                Potwierdź
              </Button>
              <Button variant="secondary" disabled={complete.isPending} onClick={closeConfirm}>
                Anuluj
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function UrgencyLabel({ item }: { item: ContractorTodoItem }) {
  if (item.is_done) {
    return (
      <span className="text-green-600 dark:text-green-400 inline-flex items-center gap-1">
        <CheckCircle2 size={13} /> wykonano{item.completed_at ? ` ${fmtDate(item.completed_at)}` : ""}
      </span>
    );
  }
  const d = item.days_left ?? 0;
  if (d < 0) return <span className="text-red-600 dark:text-red-400 font-semibold">po terminie o {-d} dn.</span>;
  if (d === 0) return <span className="text-red-600 dark:text-red-400 font-semibold">dziś</span>;
  return <span className="text-gray-500 dark:text-gray-400">zostało {d} dn.</span>;
}

function TodoRow({
  item,
  isPreview,
  urgentWindow,
  onComplete,
}: {
  item: ContractorTodoItem;
  isPreview: boolean;
  urgentWindow: number;
  onComplete?: (item: ContractorTodoItem) => void;
}) {
  const b = bucketOf(item, urgentWindow);
  // Lewa krawędź jako sygnalizacja pilności — czerwona po terminie, bursztynowa
  // dla nadchodzącego tygodnia; wykonane przygaszone.
  const edge =
    b === "overdue" ? "border-l-2 border-l-red-600"
    : b === "urgent" ? "border-l-2 border-l-amber-500"
    : "border-l-2 border-l-transparent";
  // Podgląd GK prowadzi do wewnętrznego widoku zamówienia; portal — do widoku
  // podwykonawcy. Sam payload jest w obu wypadkach identyczny.
  const href = isPreview ? `/orders/${item.order_id}` : `/contractor/orders/${item.order_id}`;

  return (
    <tr
      className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 ${edge} ${item.is_done ? "opacity-60" : ""}`}
    >
      <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">
        <span className="font-medium text-gray-900 dark:text-gray-100">{fmtDate(item.due_date)}</span>
        <span className="block mt-0.5">
          <UrgencyLabel item={item} />
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
          {item.kind === "order_deadline" ? (
            <CalendarClock size={13} className="shrink-0 text-gray-400" />
          ) : (
            <Flag size={13} className="shrink-0 text-gray-400" />
          )}
          {item.label}
        </span>
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <Link to={href} className="text-[var(--color-primary)] hover:underline">
          {item.order_number}
        </Link>
      </td>
      <td className="px-4 py-2.5">{item.localities.join(", ") || "—"}</td>
      <td className="px-4 py-2.5">
        {item.kind === "milestone" ? (
          <span className="inline-flex flex-col gap-0.5">
            <span className="inline-flex flex-wrap items-center gap-2">
              <Badge color={item.is_done ? "green" : "blue"}>{statusLabel(item)}</Badge>
              {!item.is_done && onComplete && (
                <button
                  type="button"
                  onClick={() => onComplete(item)}
                  className="text-xs text-[var(--color-primary)] hover:underline whitespace-nowrap"
                >
                  Oznacz jako ukończony
                </button>
              )}
            </span>
            {!item.is_done && item.reopened_at && (
              <span className="text-[11px] text-amber-700 dark:text-amber-400">
                cofnięte przez zlecającego {fmtDate(item.reopened_at)}
              </span>
            )}
          </span>
        ) : (
          <Badge color="gray">{statusLabel(item)}</Badge>
        )}
      </td>
    </tr>
  );
}
